import { useSeoMeta } from '@unhead/react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { parseMerchantEvent, matchHeartbeats, applyVerification, applySponsored, isActiveWithin, getCategoryInfo } from '@/lib/merchants';
import { useVerifiedMerchants } from '@/hooks/useVerifiedMerchants';
import { useSponsoredMerchants } from '@/hooks/useSponsoredMerchants';
import { MerchantDetail } from '@/components/MerchantDetail';
import { MerchantMap } from '@/components/MerchantMap';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { BchLaunchPromo } from '@/components/BchLaunchPromo';
import { ArrowLeft, MapPin, Zap } from 'lucide-react';
import NotFound from './NotFound';

export default function MerchantPage() {
  const { npub: npubParam } = useParams<{ npub: string }>();
  const navigate = useNavigate();
  const { nostr } = useNostr();

  // Decode the npub to get the hex pubkey
  let pubkey: string | null = null;
  try {
    if (npubParam) {
      const decoded = nip19.decode(npubParam);
      if (decoded.type === 'npub') {
        pubkey = decoded.data;
      }
    }
  } catch {
    // Invalid npub
  }

  // Query the merchant listing by pubkey
  const merchantQuery = useQuery({
    queryKey: ['nostr', 'merchant', pubkey],
    queryFn: async (c) => {
      if (!pubkey) throw new Error('No pubkey');

      const events = await nostr.query(
        [{ kinds: [35341], authors: [pubkey], '#t': ['bch-merchant'], limit: 1 }],
        { signal: c.signal },
      );

      if (events.length === 0) return null;

      const merchant = parseMerchantEvent(events[0]);
      if (!merchant) return null;

      // Also fetch heartbeat
      const since = Math.floor(Date.now() / 1000) - 7 * 24 * 3600;
      const heartbeats = await nostr.query(
        [{ kinds: [7531], authors: [pubkey], '#t': ['bch-merchant-activity'], since, limit: 1 }],
        { signal: c.signal },
      );

      const [enriched] = matchHeartbeats([merchant], heartbeats);
      return enriched;
    },
    enabled: !!pubkey,
    staleTime: 5 * 60 * 1000,
  });

  const verified = useVerifiedMerchants();
  const sponsored = useSponsoredMerchants();

  // Enrich with verification and sponsored status
  const merchant = (() => {
    let base = merchantQuery.data;
    if (!base) return base;
    if (verified.data) base = applyVerification([base], new Set(verified.data))[0];
    if (sponsored.data) base = applySponsored([base], new Set(sponsored.data))[0];
    return base;
  })();

  // SEO meta tags
  useSeoMeta({
    title: merchant
      ? `${merchant.name} — BCH Local`
      : 'Merchant — BCH Local',
    description: merchant?.summary
      ? merchant.summary
      : 'View this Bitcoin Cash merchant on BCH Local',
  });

  // Invalid npub
  if (!pubkey) {
    return <NotFound />;
  }

  // Loading state
  if (merchantQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <MerchantPageHeader />
        <div className="container px-4 mx-auto max-w-3xl py-8">
          <Card>
            <CardContent className="py-6">
              <div className="space-y-4">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-16 rounded-full" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
                <Skeleton className="h-[300px] w-full rounded-xl" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Merchant not found
  if (!merchant) {
    return (
      <div className="min-h-screen bg-background">
        <MerchantPageHeader />
        <div className="container px-4 mx-auto max-w-3xl py-12">
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <MapPin className="size-12 text-muted-foreground/30 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">Merchant Not Found</h2>
              <p className="text-muted-foreground max-w-sm mx-auto mb-6">
                This merchant listing doesn&apos;t exist or hasn&apos;t been published yet.
              </p>
              <Button onClick={() => navigate('/')} variant="outline">
                <ArrowLeft className="size-4 mr-2" />
                Back to Map
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const active24h = isActiveWithin(merchant, 24);
  const mappable = merchant.location !== null;

  return (
    <div className="min-h-screen bg-background">
      <MerchantPageHeader />

      <div className="container px-4 mx-auto max-w-3xl py-6 space-y-6">
        {/* Back link + status bar */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="size-4 mr-1.5" />
            All Merchants
          </Button>
          <div className="flex items-center gap-2">
            {merchant.verified && <VerifiedBadge size="md" />}
            {merchant.categories.slice(0, 2).map(cat => {
              const info = getCategoryInfo(cat);
              return (
                <Badge key={cat} variant="secondary" className="text-xs">
                  {info.emoji} {info.label}
                </Badge>
              );
            })}
            {active24h && (
              <Badge className="bg-bch text-bch-foreground text-xs">
                <Zap className="size-3 mr-0.5" />
                Active
              </Badge>
            )}
          </div>
        </div>

        {/* Map (if has location) or "no location" note */}
        {mappable ? (
          <MerchantMap
            merchants={[merchant]}
            onSelectMerchant={() => {}}
            selectedMerchant={merchant}
            className="h-[300px] sm:h-[350px]"
          />
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-8 px-6 text-center">
              <MapPin className="size-8 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                This merchant hasn&apos;t shared a map location — they may be an online or home-based business.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Detail card */}
        <Card>
          <CardContent className="py-6">
            <MerchantDetail merchant={merchant} />
          </CardContent>
        </Card>

        {/* BCH Launch promotion */}
        <BchLaunchPromo />
      </div>

      {/* Footer */}
      <footer className="border-t mt-12">
        <div className="container px-4 mx-auto max-w-3xl py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center size-5 rounded bg-bch">
                <MapPin className="size-3 text-bch-foreground" />
              </div>
              <Link to="/" className="hover:text-foreground transition-colors">BCH Local</Link>
              <span className="text-border">|</span>
              <span>Powered by Nostr</span>
            </div>
            <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
              Vibed with Shakespeare
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Shared header for the merchant page */
function MerchantPageHeader() {
  return (
    <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container flex items-center justify-between h-14 px-4 mx-auto max-w-3xl">
        <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
          <div className="flex items-center justify-center size-8 rounded-lg bg-bch">
            <MapPin className="size-4 text-bch-foreground" />
          </div>
          <span className="font-bold text-base sm:text-lg">BCH Local</span>
        </Link>
        <ThemeToggle />
      </div>
    </header>
  );
}
